/**
 * Routes, and the one session everything else reads.
 *
 * This replaced a hand-rolled screen switch that picked a view by reading query
 * parameters (`?host=1`, `?table=`). That worked while there were three screens
 * and stopped working the moment there was a library to browse, a store to come
 * back from, and a back button people expect to behave.
 *
 * The route table here is the whole route table. A URL not listed should not
 * exist.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import type { Session } from '@heroiclabs/nakama-js';
import { clientConfig } from './net/session.js';
import { configRetryDelayMs } from './net/config-retry.js';
import type { ClientConfig } from './net/session.js';
import { restoreSession, signInAsGuest } from './net/auth.js';
import { SkinProvider } from './render/skin-context.js';
import { rememberAvatars } from './render/avatars.js';
import { ErrorBoundary } from './ui/ErrorBoundary.js';
import { MarkGradients } from './render/MarkGradients.js';
import { SessionContext } from './session-context.js';
import type { SessionState } from './session-context.js';
import { Landing } from './ui/Landing.js';
import { Login } from './ui/Login.js';
import { Register } from './ui/Register.js';
import { ResetPassword } from './ui/ResetPassword.js';
import { JoinDetails } from './ui/JoinDetails.js';
import { Room } from './ui/Room.js';
import { Library } from './ui/Library.js';
import { Favourites } from './ui/Favourites.js';
import { Recent } from './ui/Recent.js';
import { AppFrame } from './ui/AppFrame.js';
import { Stats } from './ui/Stats.js';
import { Store } from './ui/Store.js';
import { GameDetail } from './ui/GameDetail.js';
import { HostGame } from './ui/HostGame.js';
import { Account } from './ui/Account.js';

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [ready, setReady] = useState(false);

  /**
   * One sign-in attempt for the whole app.
   *
   * A returning visitor keeps whatever they had — including an account. Only
   * somebody with nothing at all gets a guest identity, and they get it without
   * being asked, because a party game that opens with a sign-up form has
   * already lost half the room.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      let active: Session | null = null;
      try {
        active = (await restoreSession()) ?? (await signInAsGuest());
      } catch {
        // Offline, or the server is down. The screens say so rather than
        // hanging on a spinner forever.
      }
      if (cancelled) return;

      setSession(active);

      if (!active) {
        setReady(true);
        return;
      }

      /** One attempt. True when the config is in hand. */
      const attemptConfig = async (): Promise<boolean> => {
        try {
          const next = await clientConfig(active);
          if (!cancelled) {
            // Glyphs come from config; see `rememberAvatars`.
            rememberAvatars(next.avatars);
            setConfig(next);
          }
          return true;
        } catch {
          return false;
        }
      };

      let got = await attemptConfig();

      /*
       * The app opens whether the config arrived or not.
       *
       * Holding the splash screen through sixteen seconds of retries would
       * trade a missing button for a game nobody can reach.
       */
      setReady(true);

      /*
       * And then it keeps asking, because nothing else will.
       *
       * This used to be a bare catch with a comment claiming the screens that
       * needed it would ask again. None of them did, and the failure was close
       * to invisible: a game still dealt and played, because the avatars and
       * phrases ride with `join_room` — but the voice button was simply absent,
       * which looks exactly like voice being switched off for the room.
       */
      for (let attempt = 0; !got && !cancelled; attempt += 1) {
        const wait = configRetryDelayMs(attempt);
        if (wait === null) break;
        await new Promise((resolve) => setTimeout(resolve, wait));
        if (cancelled) return;
        got = await attemptConfig();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const adopt = useCallback(async (next: Session) => {
    setSession(next);
    try {
      const fresh = await clientConfig(next);
      rememberAvatars(fresh.avatars);
      setConfig(fresh);
    } catch {
      /* as above */
    }
  }, []);

  const value = useMemo<SessionState>(
    () => ({ session, config, ready, adopt }),
    [session, config, ready, adopt],
  );

  if (!ready) return <div className="splash">Shuffling…</div>;

  return (
    <SessionContext.Provider value={value}>
      {/*
        The app's own paint, which is Classic and has no id behind it.

        A game card in the library and the fan on the landing page draw real
        card backs, so something has to supply them outside a room. This is
        that something - and it takes no skin, because outside a room there is
        no host and therefore nobody whose deck it would be (D87).

        The table nests its own provider inside this one and wins for its
        subtree, which is what makes leaving a room take the host's paint away
        with it.

        This comment used to claim the opposite - that a skin here reached the
        table so the two could not disagree - and it was true of a world with
        one skin in it. Left as it was, it would have read as a promise that
        the library follows the deck you own. It does not, and whether it
        should is a question nobody has been asked yet.
      */}
      <SkinProvider>
        {/*
          Once, above the router.

          These are `<defs>` with ids the tone rules reference by name, so
          there has to be exactly one copy and it has to outlive any single
          route — a game mark appears on the landing strip, in the library and
          in the store, and a definition mounted per screen would blink out
          from under the icons on every navigation.
        */}
        <MarkGradients />
        {/*
          Inside the providers, outside the routes.

          A render error in one screen should cost a reload, not the session -
          and the session, the skin and those gradient defs are what a reload
          would otherwise have to rebuild. Outside the router so a crash while
          routing is caught too.
        */}
        <ErrorBoundary>
          <BrowserRouter>
          <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset" element={<ResetPassword />} />
              {/*
                The pages you move BETWEEN, inside the frame.
                
                A game's page, the join screens and the table are deliberately
                outside it: there the answer to "where am I" is not a place on
                the rail, so the rail would be furniture in the way. See
                `AppFrame`.
              */}
              <Route path="/account" element={<AppFrame><Account /></AppFrame>} />
              <Route path="/library" element={<AppFrame><Library /></AppFrame>} />
              {/* American spelling in the URL and in the copy, British in the
                  filename and the code - the app says "Favorites" and the
                  codebase says `favourites`, which is the split the rest of
                  it already lives with. */}
              <Route path="/favorites" element={<AppFrame><Favourites /></AppFrame>} />
              <Route path="/recent" element={<AppFrame><Recent /></AppFrame>} />
              <Route path="/stats" element={<AppFrame><Stats /></AppFrame>} />
              <Route path="/store" element={<AppFrame><Store /></AppFrame>} />
              <Route path="/game/:gameId" element={<GameDetail />} />
              <Route path="/host/:gameId" element={<HostGame />} />

              <Route path="/join" element={<JoinDetails />} />
              <Route path="/join/:code" element={<JoinDetails />} />
              <Route path="/play/:code" element={<Room mode="play" />} />
              <Route path="/watch/:code" element={<Room mode="watch" />} />

              {/* Anything else is a mistyped URL, not a screen. */}
              <Route path="*" element={<UnknownRoute />} />
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </SkinProvider>
    </SessionContext.Provider>
  );
}

function UnknownRoute() {
  const params = useParams();
  /*
   * A bare code in the path is almost always somebody typing the room code
   * into the address bar. Send them where they meant to go rather than to a
   * dead end.
   */
  const maybeCode = params['*'];
  if (typeof maybeCode === 'string' && /^[A-Za-z0-9]{4,8}$/.test(maybeCode)) {
    return <Navigate to={`/join/${maybeCode.toUpperCase()}`} replace />;
  }
  return <Navigate to="/" replace />;
}
